/**
 * Normalize namespace to always be explicit
 * @param {string|undefined} namespace - The namespace (public, private, pending, or undefined)
 * @returns {string} Normalized namespace (defaults to 'private' if undefined)
 */
export function normalizeNamespace(namespace) {
  if (!namespace || namespace === '') {
    return 'private';
  }
  return namespace;
}

/**
 * Centralized API client for making HTTP requests with authentication
 */
class ApiClient {
  constructor(baseURL, getToken) {
    this.baseURL = baseURL;
    this.getToken = getToken;
  }

  /**
   * Makes an authenticated HTTP request
   * @param {string} endpoint - API endpoint (e.g., '/api/items')
   * @param {object} options - Fetch options
   * @returns {Promise<any>} Response data
   * @throws {Error} HTTP error with status code
   */
  async request(endpoint, options = {}) {
    const token = this.getToken();
    const url = `${this.baseURL}${endpoint}`;

    const config = {
      ...options,
      credentials: 'include', // Send cookies with requests (for refresh_token)
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `${token.token_type} ${token.id_token}` }),
        ...options.headers,
      },
    };

    if (options.body && typeof options.body === 'object') {
      config.body = JSON.stringify(options.body);
    }

    const res = await fetch(url, config);

    if (!res.ok) {
      const error = new Error(`HTTP ${res.status}: ${res.statusText}`);
      error.status = res.status;
      error.code = String(res.status);
      error.response = res;
      const errContentType = res.headers.get('content-type');
      if (errContentType?.includes('application/json')) {
        try {
          error.data = await res.json();
          if (error.data?.error) error.message = error.data.error;
        } catch {
          // Ignore body parse failures; fall back to status-based error
        }
      }
      throw error;
    }

    const contentType = res.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      return res.json();
    }

    return res.text();
  }

  /**
   * GET request with query parameters
   */
  get(endpoint, params) {
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value);
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url = `${endpoint}?${queryString}`;
      }
    }
    return this.request(url, { method: 'GET' });
  }

  /**
   * POST request
   */
  post(endpoint, body) {
    return this.request(endpoint, { method: 'POST', body });
  }

  /**
   * PATCH request
   */
  patch(endpoint, body) {
    return this.request(endpoint, { method: 'PATCH', body });
  }

  /**
   * PUT request
   */
  put(endpoint, body) {
    return this.request(endpoint, { method: 'PUT', body });
  }

  /**
   * DELETE request
   */
  delete(endpoint, body) {
    return this.request(endpoint, { method: 'DELETE', ...(body && { body }) });
  }
}

export default ApiClient;
